import { useCallback, useContext } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { ACCEPTED_FILE_TYPES, MAX_SIZE } from '../data/DataTypes';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import { uuid4 } from '@sentry/utils';
import DataRoomDocumentsContext from '../providers/DataRoomDocuments/DataRoomDocuments.context';
import {
  addNewVersionFileConfig,
  handleFileRejections,
  updateFailedFileConfig,
  updateFileConfig,
} from '../utils';
import { AxiosError } from 'axios';
import { EnhancedFile } from 'uploadFileUtils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DataRoomUploadContext from 'router/subrouters/DataRoom/providers/DataRoomUpload/DataRoomUpload.context';
import useUpdateDataRoomCapacity from './useUpdateDataRoomCapacity';
import { useParams } from 'react-router-dom';

const useReplaceFile = (node: DataRoomNode) => {
  const { popError, popSuccess } = useContext(PopNotificationsContext);
  const {
    dataRoom: { _id },
  } = useContext(DataRoomContext);
  const { setUploaderProps } = useContext(DataRoomUploadContext);
  const { updateNodeById } = useContext(DataRoomDocumentsContext);

  const updateDataRoomCapacity = useUpdateDataRoomCapacity();

  const { documentId } = useParams();

  const { t } = useTranslation();

  const onUpload = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      handleFileRejections(_id, fileRejections, setUploaderProps, popError);

      const file = acceptedFiles[0];

      try {
        const uploadId = uuid4();

        addNewVersionFileConfig(file, node.name, documentId, setUploaderProps);

        const { dataRoomNode } = await dataRoomApi.nodes.updateNodeVersion(
          node._id,
          file,
          uploadId,
          updateFileConfig(file as EnhancedFile, setUploaderProps),
        );

        if (dataRoomNode) {
          popSuccess(t('replaceFileSuccess'));
          updateNodeById(dataRoomNode?._id, dataRoomNode);
          updateDataRoomCapacity();
        } else {
          throw Error;
        }
      } catch (e) {
        updateFailedFileConfig(
          file as EnhancedFile,
          setUploaderProps,
          e as AxiosError,
        );
      }
    },
    [
      _id,
      node,
      documentId,
      popError,
      popSuccess,
      setUploaderProps,
      t,
      updateNodeById,
      updateDataRoomCapacity,
    ],
  );

  const { getRootProps, getInputProps } = useDropzone({
    noDrag: true,
    noDragEventsBubbling: true,
    accept: ACCEPTED_FILE_TYPES,
    useFsAccessApi: false,
    maxSize: MAX_SIZE,
    multiple: false,
    maxFiles: 1,
    onDrop: onUpload,
  });

  const { onClick, ...restOfRootProps } = getRootProps();

  return {
    restOfRootProps,
    getInputProps,
    onReplaceClick: onClick,
  };
};

export default useReplaceFile;
